import type { Components } from '@mui/material/styles';
import { darkPalette } from '../palette';

type MuiAlertVariants = NonNullable<
  NonNullable<Components['MuiAlert']>['variants']
>;

/** Standard success — WDUI `palette.alert.normal` surface (not MUI’s washed `successStandardBg`). */
const successStandardVariant: MuiAlertVariants[number] = {
  props: { severity: 'success', variant: 'standard' },
  style: {
    backgroundColor: 'var(--mui-palette-alert-normal-background)',
    color: 'var(--mui-palette-text-primary)',
    '& .MuiAlert-icon': {
      color: 'var(--mui-palette-success-main)',
    },
  },
};

/** Standard info — WDUI `palette.alert.info` surface. */
const infoStandardVariant: MuiAlertVariants[number] = {
  props: { severity: 'info', variant: 'standard' },
  style: {
    backgroundColor: 'var(--mui-palette-alert-info-background)',
    color: 'var(--mui-palette-text-primary)',
    '& .MuiAlert-icon': {
      color: 'var(--mui-palette-info-main)',
    },
  },
};

/** Standard error — WDUI `palette.alert.moderate` surface (semantic “moderate” / red row). */
const errorStandardVariant: MuiAlertVariants[number] = {
  props: { severity: 'error', variant: 'standard' },
  style: {
    backgroundColor: 'var(--mui-palette-alert-moderate-background)',
    color: 'var(--mui-palette-text-primary)',
    '& .MuiAlert-icon': {
      color: 'var(--mui-palette-error-main)',
    },
  },
};

/**
 * Standard `severity="warning"` alerts: MUI's default tints `warning.light` toward paper and reads
 * as nearly unstyled; align with `background.inlineInfoCard` + readable text (same intent as
 * `WildfireAutomatedDisclaimer` and the incident changelog).
 */
const warningStandardVariant: MuiAlertVariants[number] = {
  props: { severity: 'warning', variant: 'standard' },
  style: {
    backgroundColor: 'var(--mui-palette-background-inlineInfoCard)',
    color: 'var(--mui-palette-text-primary)',
    '& .MuiAlert-icon': {
      color: 'var(--mui-palette-warning-main)',
    },
  },
};

/**
 * WDUI custom severity — uses tokens under `palette.alert.neutral` (lowercase `alert`).
 * Kept as theme `variants` so they merge with MUI's built-in severity styles (unlike nesting
 * `variants` under `styleOverrides.root`, which replaces the root style object and can drop
 * default Alert surface colors).
 */
const watchdutyNeutralAlertVariants: MuiAlertVariants = [
  {
    props: { severity: 'watchduty-neutral', variant: 'filled' },
    style: {
      backgroundColor: 'var(--mui-palette-alert-neutral-background)',
    },
  },
  {
    props: { severity: 'watchduty-neutral', variant: 'standard' },
    style: {
      backgroundColor: 'var(--mui-palette-alert-neutral-background)',
    },
  },
  {
    props: { severity: 'watchduty-neutral', variant: 'outlined' },
    style: {
      border: '1px solid var(--mui-palette-alert-neutral-border)',
    },
  },
];

/**
 * WDUI custom severity - fixed "minor" look (dark-theme colors) regardless of light/dark mode,
 * for alerts like `PersistentStatusAlert`. `main`/`background` come from `darkPalette.alert.minor`
 * to stay in sync with the theme; text uses `darkPalette.text.primary` for readable contrast.
 */
const minorFixed = darkPalette.alert.minor;
const minorFixedContrastText = darkPalette.text!.primary!;

const watchdutyMinorAlertVariants: MuiAlertVariants = [
  {
    props: { severity: 'watchduty-minor', variant: 'filled' },
    style: {
      backgroundColor: minorFixed.main,
      color: minorFixedContrastText,
    },
  },
  {
    props: { severity: 'watchduty-minor', variant: 'standard' },
    style: {
      backgroundColor: minorFixed.background,
      color: minorFixedContrastText,
      '& .MuiAlert-icon': {
        color: minorFixed.main,
      },
    },
  },
  {
    props: { severity: 'watchduty-minor', variant: 'outlined' },
    style: {
      border: `1px solid ${minorFixed.main}`,
      '& .MuiAlert-icon': {
        color: minorFixed.main,
      },
    },
  },
];

export const MuiAlert: Components['MuiAlert'] = {
  styleOverrides: {
    root: {
      borderRadius: 12,
      // Let `.MuiAlert-icon` keep MUI severity colors; `inherit` here was overriding them.
      '& .MuiAlert-message, & .MuiAlert-action': {
        color: 'inherit',
      },
      '& .MuiAlertTitle-root': {
        color: 'inherit',
      },
    },
  },
  variants: [
    successStandardVariant,
    infoStandardVariant,
    errorStandardVariant,
    warningStandardVariant,
    ...watchdutyNeutralAlertVariants,
    ...watchdutyMinorAlertVariants,
  ],
};
